import React from 'react';
import config from '../../config';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import { ContributoryIRAAccountTypes } from '../../flows/contributory/util';
import { getURLTickers, getRetirementTypeDisplay } from '../ProfileScreen/util';
import { navigateToLoginFromChecklist } from './utils';
import styles from './styles.css';
import { redirectToAccountFinder } from '../../flows/shared/utils';

const StepsSection = ({ iraAccountType }) => {
  return (
    <>
      <section>
        <div className="checklist-section-title">
          Opening an account takes 5-10 minutes:
        </div>
        <dl>
          <ol>
            <li>
              <dt data-name="1">Provide basic investment details</dt>
              <dd>This helps us tailor your investment options.</dd>
            </li>
            <li>
              <dt data-name="2">Choose Mutual Funds for your IRA</dt>
              <dd>
                <a
                  href="https://www.troweprice.com/personal-investing/tools/fund-research"
                  target="_blank"
                  rel="noopener noreferrer">
                  Research Mutual Funds
                </a>{' '}
                or{' '}
                <a
                  href={`${config(
                    'returnUrlPrefix'
                  )}personal-investing/new-account/setup/account-info/brk-checklist/${
                    iraAccountType === ContributoryIRAAccountTypes.ROTH
                      ? 'roth-ira'
                      : 'traditional-ira'
                  }`}
                  target="_blank"
                  rel="noopener noreferrer">
                  switch to a Brokerage{' '}
                  {getRetirementTypeDisplay(iraAccountType)} account
                </a>{' '}
                to invest in ETFs and stocks.
              </dd>
            </li>
            <li>
              <dt data-name="3">Provide personal details</dt>
              <dd>We&apos;ll use these to secure and verify your account.</dd>
            </li>
          </ol>
        </dl>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const ChecklistSection = () => {
  return (
    <>
      <section>
        <div className="checklist-section-title">You will need:</div>
        <ul>
          <li>Social Security number</li>
          <li>Permanent U.S. residence</li>
          <li>A minimum of $1,000</li>
        </ul>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const getHeaderText = iraAccountType =>
  `Opening a ${getRetirementTypeDisplay(iraAccountType)} account`;

const getSubHeaderText = iraAccountType =>
  iraAccountType === ContributoryIRAAccountTypes.ROTH
    ? "A Roth IRA lets you take tax-free withdrawals from qualified distributions. It may be a good option if you're in a lower tax bracket. "
    : "Invest for later and receive potential tax deductions now. A Traditional IRA may be a good option if you're in a higher tax bracket. ";

const ContribChecklistScreen = ({ appState, next }) => {
  const {
    store: {
      authenticated,
      app: { iraAccountType }
    }
  } = appState;

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);

  const renderChecklistCTAs = () => (
    <div className="button-container">
      {authenticated ? (
        <MFBeaconButton
          variant="primary"
          size="normal"
          id="checklistContinue"
          className="beacon-button-margin-override"
          onClick={next(multiBuyTickers)}
          label="Continue Opening an Account"
        />
      ) : (
        <div className="login-buttons">
          <MFBeaconButton
            variant="primary"
            size="normal"
            id="checklistLogin"
            className="beacon-button-margin-override"
            onClick={() => navigateToLoginFromChecklist('/profile')}
            label="Already a Client? Log In to Continue"
          />
          <MFBeaconButton
            variant="secondary"
            size="normal"
            id="checklistContinueNew"
            className="beacon-button-margin-override"
            onClick={next(multiBuyTickers)}
            label="New to T. Rowe Price? Start Now"
          />
        </div>
      )}
      <MFBeaconButton
        variant="secondary"
        size="normal"
        id="checklistBack"
        className="beacon-button-margin-override"
        onClick={redirectToAccountFinder}
        label="Back"
      />

      <style jsx>{styles}</style>
    </div>
  );

  return (
    <div className="checklist-screen">
      <Heading text={getHeaderText(iraAccountType)} />
      <SubHeading text={getSubHeaderText(iraAccountType)}>
        <a
          href="https://www.troweprice.com/personal-investing/accounts/retirement/index.html"
          target="_blank"
          rel="noopener noreferrer">
          Learn more about our retirement solutions.
        </a>
      </SubHeading>

      <div className="qv-row">
        <div className="qv-col col-left">
          <StepsSection iraAccountType={iraAccountType} />
        </div>

        <div className="qv-col col-right">
          <ChecklistSection />
        </div>
      </div>

      {renderChecklistCTAs()}

      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default ContribChecklistScreen;
